'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon, isIconName } from '../shared/Icon';
import {
  headingLevel,
  items,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Trust figures: five equal columns, each an icon, a figure and a label.
 *
 * <p>Figures such as years of trading or guarantee lengths are FACTS the owner
 * must confirm; the template marks them `[confirm]`. The section heading is
 * visually hidden by default (the figures speak for themselves on screen) but
 * always present for screen-reader navigation.
 *
 * <p>Layout: two columns on a phone, three on a tablet, five from 1024 px.
 */
export interface SpatialKitchenTrustFigure {
  icon?: string;
  figure?: Localized;
  label?: Localized;
}

export interface SpatialKitchenTrustFiguresProps {
  title?: Localized;
  showTitle?: boolean;
  figures?: SpatialKitchenTrustFigure[];
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-trust__grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:2rem 1rem}
@media (min-width:640px){.sk-trust__grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (min-width:1024px){.sk-trust__grid{grid-template-columns:repeat(5,minmax(0,1fr));gap:1.5rem}}
.sk-trust__item{display:grid;justify-items:center;text-align:center;gap:.5rem;padding:0 .5rem}
.sk-trust__icon{display:inline-grid;place-items:center;width:3.25rem;height:3.25rem;border-radius:999px;background:var(--sk-white);color:var(--sk-sage-deep);margin-bottom:.25rem}
.sk-trust__figure{font-family:var(--sk-serif);font-weight:400;font-size:clamp(1.375rem,1.2rem + .6vw,1.75rem);line-height:1.2;color:var(--sk-ink)}
.sk-trust__label{font-size:.9375rem;color:var(--sk-muted);line-height:1.45}
@media (min-width:1024px){.sk-trust__item + .sk-trust__item{border-left:1px solid var(--sk-border)}}
`;

export function SpatialKitchenTrustFigures(props: SpatialKitchenTrustFiguresProps) {
  const figures = items<SpatialKitchenTrustFigure>(props.figures).slice(0, 5);
  const title = text(props.title, 'Why customers choose us');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-trust sk-section ${surfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="trust-figures" css={CSS} />
      <div className="sk-container">
        <Title
          id={titleId}
          className={props.showTitle ? 'sk-heading sk-h2 sk-section-head sk-section-head--centre' : 'sk-visually-hidden'}
        >
          {title}
        </Title>
        <ul className="sk-trust__grid sk-list-reset">
          {figures.map((item, index) => {
            const figure = text(item.figure);
            const label = text(item.label);
            return (
              <li key={`${figure}-${index}`} className="sk-trust__item">
                <span className="sk-trust__icon">
                  <Icon name={isIconName(item.icon) ? item.icon : 'shield'} size={24} />
                </span>
                <span className="sk-trust__figure">{figure}</span>
                {label && <span className="sk-trust__label">{label}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
